import { usePermissions } from "../hooks/usePermissions";
import SubtaskChecklist from "./SubtaskChecklist";
import { subtaskTiming } from "../lib/subtaskTiming"; // brief 91 §3a
import type { TimerRowShape } from "../lib/subtaskTiming";
import { RAD, FS } from "../lib/tokens";

// Phase 23 — event subtask affordances for the agenda surfaces (standalone
// AgendaView, Dashboard, Case View, Collective Case View).
//
// D2-correction: subtasks expand INLINE, in the list flow, pushing the rows
// below down — exactly like the Tasks pane (tkExpanded + inline
// SubtaskChecklist). NO portal / floating popover. The host surface owns the
// expand state (a Set) and renders <EventSubtasks> as a sibling DIV right after
// the event row; SubtaskCounter is just the count/expander badge (controlled).

// Event-subtask write eligibility (mirrors the server's canEditSubtaskOfEvent:
// admin / RP-of-case-or-cc / creator / assigned). Single source of truth shared
// by EventSubtasks (panel write gate) and the empty-state add affordance below.
export function canWriteEventSubtasks(viewer: any, event: any) {
  if (!viewer || !event) return false;
  if (viewer.isAdmin === true) return true;
  const rpc = Array.isArray(viewer.myRpCaseIds) ? viewer.myRpCaseIds : [];
  const rcc = Array.isArray(viewer.myRpCcIds) ? viewer.myRpCcIds : [];
  if (event.caId != null && rpc.includes(event.caId)) return true;
  if (event.caseId != null && rpc.includes(event.caseId)) return true;
  if (event.ccId != null && rcc.includes(event.ccId)) return true;
  if (event.createdById != null && event.createdById === viewer.id) return true;
  const asg = event.assignedCollaborators || event.assigned || [];
  if (Array.isArray(asg) && asg.includes(viewer.id)) return true;
  return false;
}

// Controlled count + expander badge. Click toggles the host's inline expansion
// (stopPropagation so the row's jump-to-case doesn't fire).
//
// Empty state: when there are no subtasks the badge is suppressed on read-only
// surfaces (Agenda, Dashboard) — but a host that can add subtasks passes
// `canAdd` to surface a dashed "+" affordance so the panel opens and the
// first subtask can be created (instead of a dead, non-actionable gap).
//
// ── BRIEF 105 §2b — THE ARROW IS GONE, BOTH BRANCHES ────────────────────────
//
// Laurent: "the arrow ... is useless." It said open-or-closed and so does the
// BACKGROUND, which changes on the same state and is still here: `--as` open,
// `--bi` closed. Two signals for one bit, and the one that was removed is the
// one that costs a character of width on every row.
//
// ── §2c — AND THE WIDTH IS THE ACTUAL DEFECT ────────────────────────────────
//
// "the whole task row jumps around ... visual eyesore." The badge carried
// `flexShrink: 0` and NO WIDTH, so it was as wide as its text: a task with no
// subtasks and one with `9/12` reserved different amounts and everything after
// them moved. MEASURED in a browser at this exact font, size, padding and
// border (JetBrains Mono 9px, `1px 5px`, 1px border — 5px per character,
// 12px of chrome):
//
//     "+"        17px        "0/10" · "9/12" · "0/99"    32px
//     "0/2"      27px        "12/34" · "99/99" · "0/100" 37px
//
// So **37** is the widest counted state that occurs, and it is what both
// branches reserve. `min-width` DOES NOT APPLY to a non-replaced inline
// element, which is why both branches are `inline-flex` now — declaring the
// width on the spans as they were would have changed nothing at all and looked
// like a fix.
//
// Past 99/99 the badge grows and pushes as it does today. That is the right
// failure: reserve for the range that occurs, not for the one that cannot.
const COUNTER_MIN_WIDTH = 37;
/* 195 §A — the counted badge's hover sentence, built plainly; the click clause
   only when the counter is a control (an archived card's is not). */
function counterTitle(done: number, total: number, od: number, due: number, open: boolean, clickable: boolean): string {
  let s = `${done}/${total} subtasks done`;
  if (od) s += `, ${od} overdue`;
  if (due) s += `, ${due} due soon`;
  if (clickable) s += ` — click to ${open ? "hide" : "show"} subtasks`;
  return s;
}
type SubtaskCounterProps = {
  subtasks?: any[];
  todayStr: string;
  open?: boolean;
  onToggle?: () => void;
  canAdd?: boolean;
};

export default function SubtaskCounter({
  subtasks,
  todayStr,
  open,
  onToggle,
  canAdd = false,
}: SubtaskCounterProps) {
  // Round 13 (C75) — archived subtasks leave every counter/badge.
  const subs = (Array.isArray(subtasks) ? subtasks : []).filter(
    (s) => s && !s.archived,
  );
  if (subs.length === 0) {
    if (!canAdd) return null;
    return (
      <span
        title={`No subtasks yet — click to ${open ? "hide" : "add the first"}`}
        onClick={(e) => {
          e.stopPropagation();
          if (typeof onToggle === "function") onToggle();
        }}
        style={{
          fontSize: FS.s9,
          padding: "1px 5px",
          borderRadius: RAD.r8,
          background: open ? "var(--as)" : "var(--bi)",
          color: "var(--tm)",
          border: "1px dashed var(--br)",
          fontFamily: "'JetBrains Mono',monospace",
          flexShrink: 0,
          cursor: "pointer",
          /* §2c — the empty branch reserves exactly what the filled one does,
             which is the whole point: a row with subtasks and a row without
             must not put their titles in different places. */
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: COUNTER_MIN_WIDTH,
        }}
      >
        +
      </span>
    );
  }

  let _od = 0,
    _due = 0,
    _open = 0;
  subs.forEach((st) => {
    if (st.done) return;
    _open++;
    if (!st.dueDate) return;
    const _d = String(st.dueDate).slice(0, 10);
    if (_d < todayStr) _od++;
    else {
      const _diff = Math.round(
        (new Date(_d).getTime() - new Date(todayStr).getTime()) / 864e5,
      );
      if (_diff <= 3) _due++;
    }
  });
  const _done = subs.length - _open;
  const _color = _od > 0 ? "var(--rd)" : _due > 0 ? "var(--am)" : "var(--tm)";
  const clickable = typeof onToggle === "function";

  return (
    <span
      title={counterTitle(_done, subs.length, _od, _due, !!open, clickable)}
      onClick={(e) => {
        e.stopPropagation();
        if (typeof onToggle === "function") onToggle();
      }}
      style={{
        fontSize: FS.s9,
        padding: "1px 5px",
        borderRadius: RAD.r8,
        background: open ? "var(--as)" : "var(--bi)",
        color: _color,
        border: `1px solid ${_color}`,
        fontFamily: "'JetBrains Mono',monospace",
        flexShrink: 0,
        /* 195 §A — an archived card's counter is INERT (no onToggle): it still
           says 2/4, and its cursor and title stop promising a click. */
        cursor: clickable ? "pointer" : "default",
        /* §2c — 37px, measured; see the header. */
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: COUNTER_MIN_WIDTH,
      }}
    >
      {_done}/{subs.length}
    </span>
  );
}

// Inline editable subtask list for an AgendaEvent — rendered by the host as a
// sibling below the event row when expanded (mirrors the Tasks pane). Wraps the
// shared SubtaskChecklist (parentEventId). Eligibility mirrors the server's
// canEditSubtaskOfEvent (admin / RP-of-case-or-cc / creator / assigned); a
// non-eligible viewer gets the read-only list (no optimistic-then-403 flicker).
// BRIEF 90 §3a — AND THE BRIEF'S MEASUREMENT OF THIS IS WRONG, IN A WAY THAT
// MAKES THE FIX FOUR PROPS INSTEAD OF A MIGRATION.
//
// §3 reads it as two implementations — "EventSubtasks 152 lines" against
// "SubtaskChecklist 998 lines", "one is six times the other". The 152 is this
// whole FILE, which also holds `SubtaskCounter` and `canWriteEventSubtasks`.
// `EventSubtasks` itself is twenty lines and its entire body is
// `<SubtaskChecklist parentEventId=… />`. **There is no second implementation.
// All five call sites already mount the component the brief asks them to adopt**,
// through this one wrapper, and have since it was written.
//
// THE DEFECT IS REAL AND THE DIAGNOSIS IS NOT. The brief's chain —
// "SubtaskCounter.tsx has ZERO refs to assignedCoId / AssigneePicker /
// InitialsBadge → EventSubtasks CANNOT render an assignee" — is a grep over a
// file whose component DELEGATES. The refs are absent because the rendering is
// somewhere else, not because it is missing.
//
// What was actually missing is FOUR PROPS. The task drawer
// (`CaseTaskRow.tsx:1129-1132`) passes `co`, `currentUser`, `assignable` and
// `canAssignAll`; this wrapper passed none of them, so:
//
//   SubtaskChecklist.tsx:135   showPicker = assignable && writable  → FALSE,
//                              so no picker: the assignment cannot be MADE here
//   SubtaskChecklist.tsx:689   the read-only branch DOES exist and renders an
//                              InitialsBadge — but it resolves the id against
//                              `co`, and `co` was undefined, so `_a` came back
//                              undefined and it rendered null
//
// So the assignee was invisible for TWO reasons and both are the same omission:
// the component could always have shown it and was never handed the list to
// resolve it against. That is brief 86 §2e-1's finding one layer out — the Event
// modal likewise "simply never passed the four props the Task modal passes".
//
// MEASURED BEFORE CHANGING ANYTHING: 15 event subtasks on this box already carry
// an `assignedCoId` — 1 on tenant 1 and 14 on the fixture tenant 4. Every one is
// an assignment that GRANTED ROW-LEVEL SIGHT of its event (`alertEngine.js:313`:
// rowAssigneeUnion has always counted subtasks[].assignedCoId) and that nobody
// looking at the event could see. They become visible with this commit; no
// migration is involved, because nothing about the data was wrong.
//
// `canAssignAll` mirrors the Event MODAL's gate exactly (`caseView.agenda.edit`,
// brief 86 §2e-1) rather than `canWrite`: the two answer different questions —
// may I edit this list, versus may I assign to ANYONE rather than to myself —
// and the modal's answer is already ruled for this surface's own subject.
// ── BRIEF 91 §3a — THE SECOND TIME, AND WHY THE FIX IS NOT FIVE PROPS ───────
//
// Brief 90 fixed the four props above. The TIMER half was not fixed, and "copy
// the task machinery" has now read as done twice while being false both times.
// The measurement: `CaseTaskRow.tsx` passed `onLogTime` · `onStartTimer` ·
// `isTimerRunning` · `runningSubtaskId` · `subtaskTimers`; this wrapper passed
// none of them, so an event's subtask could be assigned, ticked, dated and
// archived, and could not be timed or logged — the one thing a law firm's
// subtask is for.
//
// SO THE WRAPPER IS FIXED, NOT THE FIVE PROPS. `subtaskTiming` (a required
// argument list, src/lib/subtaskTiming.ts) builds the group as ONE value, from
// the same derivations that used to live only inside `CaseTaskRow`; this
// wrapper spreads it, and a partial omission of the group is now a typecheck
// error rather than a missing button. The three ingredients it needs —
// `activeTimers`, `startTimer`, `openModal` — arrive as props, because they are
// the host's own state and every host already holds them for the task rows it
// renders beside these.
//
// A HOST THAT PASSES NONE OF THE THREE STILL GETS THE OLD BEHAVIOUR, on
// purpose: `_hasTimerCol` inside `SubtaskChecklist` is driven by whether the
// callbacks are functions, so a surface with no timer of its own (a read-only
// list) renders exactly the row it rendered before. That is the residue this
// approach cannot remove and the PR body says so.
type EventSubtasksProps = {
  event?: any;
  onItemsChange?: (next: unknown[]) => void;
  // The two the assignee column needs: the list to resolve an id against, and
  // who "me" is for the ring.
  co?: readonly { id?: number }[];
  currentUser?: number | null;
  // §3a — the three the timer column needs, and the ONLY three: everything else
  // it wants is derived from them by `subtaskTiming`.
  activeTimers?: readonly TimerRowShape[] | null;
  startTimer?: (taskId?: unknown, subtaskId?: unknown, opts?: unknown) => void;
  openModal?: (kind: unknown, seed?: unknown) => void;
  // 109 §1c — the host's lock state, forwarded to SubtaskChecklist: structure
  // (text/date/add/archive/assignee) follows it, the done checkbox does not.
  locked?: boolean;
  /* 216 addendum 7 §11 (2) [RULED 84] — inside the Dashboard's card (CaseEventRow `dashboardCard`): the wash
     without the attached frame, the card being the frame. */
  inside?: boolean;
  // 110 §1b — the Dashboard's view-only rule, forwarded to SubtaskChecklist.
  readOnly?: boolean;
};

export function EventSubtasks({
  event,
  onItemsChange,
  co,
  currentUser,
  activeTimers,
  startTimer,
  openModal,
  locked = false,
  // 110 §1b — the Dashboard's view-only rule, passed through verbatim.
  readOnly = false,
  inside = false,
}: EventSubtasksProps) {
  const { viewer, can } = usePermissions();
  if (!event || event.id == null) return null;

  const canWrite = canWriteEventSubtasks(viewer, event);
  const _me = currentUser ?? viewer?.id ?? null;
  /* §3a — the timer group, or nothing. A host that hands over `startTimer` and
     `openModal` gets the identical column the task drawer has; one that does
     not gets today's row, because `SubtaskChecklist` keys the column off the
     callbacks' presence. */
  const timing =
    typeof startTimer === "function" && typeof openModal === "function"
      ? subtaskTiming({
          kind: "event",
          parentId: event.id,
          caseId: event.caseId ?? event.caId ?? null,
          ccId: event.ccId ?? null,
          type: event.type ?? null,
          currentUser: _me,
          activeTimers,
          startTimer,
          openModal,
        })
      : null;

  return (
    /* brief 90 §3b — the event drawer had NO background at all, so the two
       panels did not agree on having one. Same class as the task drawer's.
       BRIEF 91 §3b — and it had the colour without the SHAPE: a self-contained
       rounded card with a second box floating under it. `evSubDrawer` is the
       half `.subtaskDrawer` cannot carry, because that class is also on a `<tr>`
       inside the tasks table, where a border and a radius would be wrong. It
       supplies three sides, the two bottom corners and the padding this style
       attribute used to hold — one declaration, in App.css beside the rule that
       squares the card above it. */
    <div className={inside ? "subtaskDrawer evDrawerIn" : "subtaskDrawer evSubDrawer"}>
      <SubtaskChecklist
        {...({
          parentEventId: event.id,
          initialSubtasks: event.subtasks || [],
          canWrite,
          locked,
          readOnly,
          isAdmin: !!viewer?.isAdmin,
          co: co || [],
          currentUser: _me,
          assignable: true,
          ...(timing || {}),
          canAssignAll:
            !!viewer?.isAdmin ||
            !!can("caseView.agenda.edit", {
              caseId: event.caseId ?? event.caId ?? null,
              ccId: event.ccId ?? null,
            }),
          onItemsChange,
        } as any)}
      />
    </div>
  );
}
